import { WarningOutlined } from '@ant-design/icons';
import { t } from '@hefe/i18n';
import {
  AnalysisIssue,
  AnalysisIssueBasketballEventGymClosed,
  AnalysisIssueBasketballGameTbd,
  AnalysisIssuePersonMultiEventConflict,
  AnalysisIssuePersonSingleEventConflict,
} from '@hefe/models';
import { IssuesApi } from '@hefe/store';
import { colors } from '@hefe/ui';
import { formatTimeAgo } from '@hefe/utils';
import { Button, Card, Flex, List, Space, Tag, Typography } from 'antd';
import { useMemo } from 'react';
import { severityColors, severityLabels } from '../../utils/isue.utils';
import IssueBasketballEventGymClosedBody from '../issue-basketball-event-gym-closed/issue-basketball-event-gym-closed.component';
import IssueBasketballGameTbdBody from '../issue-basketball-game-tbd/issue-basketball-game-tbd.component';
import IssuePersonMultiEventConflictBodyComponent from '../issue-person-multi-event-conflict/issue-person-multi-event-conflict.component';
import IssuePersonSingleEventConflictBodyComponent from '../issue-person-single-event-conflict/issue-person-single-event-conflict.component';

interface IssueListItemProps {
  issue: AnalysisIssue;
}

function IssueBody({ issue }: IssueListItemProps) {
  switch (issue.type) {
    case 'basketball_event_gym_closed':
      return (
        <IssueBasketballEventGymClosedBody
          issue={issue as AnalysisIssueBasketballEventGymClosed}
        />
      );
    case 'basketball_game_tbd':
      return (
        <IssueBasketballGameTbdBody
          issue={issue as AnalysisIssueBasketballGameTbd}
        />
      );
    case 'person_multi_event_conflict':
      return (
        <IssuePersonMultiEventConflictBodyComponent
          issue={issue as AnalysisIssuePersonMultiEventConflict}
        />
      );
    case 'person_single_event_conflict':
      return (
        <IssuePersonSingleEventConflictBodyComponent
          issue={issue as AnalysisIssuePersonSingleEventConflict}
        />
      );
    default:
      return null;
  }
}

export default function IssueListItemComponent(props: IssueListItemProps) {
  const { issue } = props;

  const [acknowledgeIssue, { isLoading: isAcknowledging }] =
    IssuesApi.useAcknowledgeIssueMutation();
  const [hideIssue, { isLoading: isHiding }] = IssuesApi.useHideIssueMutation();
  const [unhideIssue, { isLoading: isUnhiding }] =
    IssuesApi.useUnhideIssueMutation();

  const contextPreview = useMemo(() => {
    if (!issue.context || typeof issue.context !== 'object') {
      return '—';
    }

    const entries = Object.entries(issue.context).slice(0, 2);
    if (entries.length === 0) {
      return '—';
    }

    return entries
      .map(
        ([key, value]) =>
          `${key}: ${
            typeof value === 'object' ? JSON.stringify(value) : String(value)
          }`
      )
      .join(' • ');
  }, [issue.context]);

  const color = useMemo(() => {
    switch (issue.severity) {
      case 'error':
        return colors.red;
      case 'warning':
        return colors.orange;
      case 'info':
        return colors.blue;
      default:
        return colors.grey;
    }
  }, [issue.severity]);

  return (
    <List.Item style={{ padding: 0, marginBottom: 12 }}>
      <Card
        size="small"
        hoverable
        styles={{ body: { padding: 16 } }}
        style={{
          width: '100%',
          borderLeft: `4px solid ${color}`,
          cursor: 'pointer',
        }}
      >
        <Flex vertical gap={12}>
          <Flex justify="space-between" align="center">
            <Space size={8}>
              <WarningOutlined />

              <Typography.Text strong>
                {t('issues.types.' + issue.type, {
                  defaultValue: issue.type,
                })}
              </Typography.Text>
            </Space>

            <Tag color={severityColors[issue.severity]}>
              {severityLabels[issue.severity]}
            </Tag>
          </Flex>

          <Flex justify="space-between" align="center">
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('issues.status.text', {
                status: t('issues.status.types.' + issue.status),
              })}
            </Typography.Text>
            <Typography.Text style={{ fontSize: 10 }}>
              {formatTimeAgo(issue.first_detected_at)}
            </Typography.Text>
          </Flex>

          <IssueBody issue={issue} />

          {![
            'basketball_event_gym_closed',
            'basketball_game_tbd',
            'person_multi_event_conflict',
            'person_single_event_conflict',
          ].includes(issue.type) && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {contextPreview}
            </Typography.Text>
          )}

          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t('issues.acknowledged.' + (issue.acknowledged ? 'yes' : 'no'))} •{' '}
            {t('issues.hidden.' + (issue.hidden ? 'yes' : 'no'))}
          </Typography.Text>

          <Space size={8} onClick={(event) => event.stopPropagation()}>
            {!issue.acknowledged && (
              <Button
                size="small"
                loading={isAcknowledging}
                onClick={() => acknowledgeIssue(issue.id)}
              >
                {t('issues.actions.acknowledge')}
              </Button>
            )}

            {!issue.hidden ? (
              <Button
                size="small"
                loading={isHiding}
                onClick={() => hideIssue(issue.id)}
              >
                {t('issues.actions.hide')}
              </Button>
            ) : (
              <Button
                size="small"
                loading={isUnhiding}
                onClick={() => unhideIssue(issue.id)}
              >
                {t('issues.actions.unhide')}
              </Button>
            )}
          </Space>
        </Flex>
      </Card>
    </List.Item>
  );
}
